/**
 * ObjectivesConfigModal Component
 *
 * Modal for configuring objectives settings from the PreTrading dashboard.
 * Allows setting maxObjectives and pinObjectivesToLive.
 *
 * Key behaviors:
 * - Manual Save/Discard pattern
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - Navigation button disabled while dirty (with tooltip)
 * - Validation: maxObjectives must be >= 1
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Link,
  Switch,
  Tooltip,
  useToastNotification,
} from '@enfomentis/ui';

// ============================================================================
// Types
// ============================================================================

export interface ObjectivesConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when config is saved */
  onSave: (maxObjectives: number, pinObjectivesToLive: boolean) => Promise<void>;
  /** Initial maxObjectives value from settings */
  initialMaxObjectives: number;
  /** Initial pinObjectivesToLive value from settings */
  initialPinObjectivesToLive: boolean;
}

/**
 * Local state shape for dirty tracking
 */
interface LocalConfig {
  maxObjectives: number;
  pinObjectivesToLive: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ObjectivesConfigModal: React.FC<ObjectivesConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialMaxObjectives,
  initialPinObjectivesToLive,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  // Local editing state
  const [localConfig, setLocalConfig] = useState<LocalConfig>({
    maxObjectives: initialMaxObjectives,
    pinObjectivesToLive: initialPinObjectivesToLive,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Validation state
  const [validationError, setValidationError] = useState<string | null>(null);

  // Baseline for dirty tracking (stores JSON string)
  const baselineKeyRef = useRef<string>('');

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Compute dirty state using stable JSON comparison
  const localConfigKey = useMemo(() => JSON.stringify(localConfig), [localConfig]);
  const isDirty = localConfigKey !== baselineKeyRef.current;

  // Check if save should be disabled
  const hasSaveBlockingErrors = validationError !== null;

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Reset to initial values
      const config: LocalConfig = {
        maxObjectives: initialMaxObjectives,
        pinObjectivesToLive: initialPinObjectivesToLive,
      };

      setLocalConfig(config);
      baselineKeyRef.current = JSON.stringify(config);

      // Reset state
      setValidationError(null);
      setIsSaving(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialMaxObjectives, initialPinObjectivesToLive]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateMaxObjectives = useCallback((value: number): string | null => {
    if (value < 1) {
      return 'Must be at least 1';
    }
    if (!Number.isInteger(value)) {
      return 'Must be a whole number';
    }
    return null;
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleMaxObjectivesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const numValue = parseInt(rawValue, 10);

      // Handle empty or invalid input
      if (rawValue === '' || isNaN(numValue)) {
        setLocalConfig((prev) => ({ ...prev, maxObjectives: 0 }));
        setValidationError('Must be at least 1');
        return;
      }

      setLocalConfig((prev) => ({ ...prev, maxObjectives: numValue }));
      setValidationError(validateMaxObjectives(numValue));
    },
    [validateMaxObjectives]
  );

  const handlePinToggle = useCallback((isSelected: boolean) => {
    setLocalConfig((prev) => ({ ...prev, pinObjectivesToLive: isSelected }));
  }, []);

  const handleNavigateToLibrary = useCallback(() => {
    // Close modal first, then navigate
    onClose();
    navigate('/settings/workflow/objectives');
  }, [onClose, navigate]);

  const handleSave = useCallback(async () => {
    // Validate before saving
    const error = validateMaxObjectives(localConfig.maxObjectives);
    if (error) {
      setValidationError(error);
      showToast({
        title: 'Validation error',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      await onSave(localConfig.maxObjectives, localConfig.pinObjectivesToLive);

      // On successful save: update baseline (clears dirty state), then close
      baselineKeyRef.current = JSON.stringify(localConfig);
      onClose();
    } catch (err) {
      // On save failure: keep modal open, show error toast
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localConfig, validateMaxObjectives, onSave, onClose, showToast]);

  const handleDiscard = useCallback(() => {
    // Reset localConfig to baseline (clears dirty), then close modal
    const baseline = JSON.parse(baselineKeyRef.current) as LocalConfig;
    setLocalConfig(baseline);
    setValidationError(null);
    onClose();
  }, [onClose]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      isDismissable={!isDirty}
      hideCloseButton={isDirty}
      isKeyboardDismissDisabled={isDirty}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Objectives Settings</span>
        </ModalHeader>

        <ModalBody className="gap-6">
          {/* Max Objectives Input */}
          <Input
            type="number"
            label="Maximum Objectives Per Session"
            description="How many objectives can be selected for each trading session"
            value={localConfig.maxObjectives.toString()}
            onChange={handleMaxObjectivesChange}
            min={1}
            isInvalid={!!validationError}
            errorMessage={validationError}
            classNames={{ input: 'text-base' }}
          />

          {/* Pin to Live Dashboard Toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Show on Live Dashboard</p>
              <p className="text-sm text-default-500">
                Display objectives on the live trading dashboard
              </p>
            </div>
            <Switch
              isSelected={localConfig.pinObjectivesToLive}
              onValueChange={handlePinToggle}
              aria-label="Show objectives on live dashboard"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-default-200" />

          {/* Navigate to Objectives Library */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Objectives Library</p>
              <p className="text-sm text-default-500">
                Create and manage your trading objectives
              </p>
            </div>
            <Tooltip
              content="Save or discard changes first"
              isDisabled={!isDirty}
            >
              <div>
                <Link
                  onPress={handleNavigateToLibrary}
                  isDisabled={isDirty}
                  showAnchorIcon
                  size="sm"
                  className="cursor-pointer data-[disabled=true]:cursor-not-allowed"
                >
                  Manage Objectives
                </Link>
              </div>
            </Tooltip>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isDirty || isSaving || hasSaveBlockingErrors}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
